/* Silvery Silver collection page behaviour (UI Design System section 8).
   - Filters (Availability, Price) and Sort update the page in place through Shopify's Section Rendering API
     (?section_id=), keep shareable URLs (pushState), and always start again from page 1.
   - Load More appends the next page (24 products), skips any product already shown, updates the count and progress,
     moves focus to the first new card and announces the new count. The button is a real ?page=N link, so the page
     still works without JavaScript and stays crawlable.
   - Below 990 px: sticky Filter / Sort bar under the header, each opening a bottom sheet.
   Nothing here touches product forms: quick add keeps using Dawn's product-form.js and the cart notification. */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function Collection(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.busy = null;
    this.moreBusy = false;
    root.classList.add('ss-js');
    // Sheets live outside the sticky controls so they are never trapped under the header's stacking layer.
    root.querySelectorAll('[data-ss-sheet]').forEach(function (sheet) { root.appendChild(sheet); });
    this.bind();
    this.initDescription();
    this.initStickyTop();
  }

  /* ---------- URL helpers ---------- */
  Collection.prototype.currentParams = function () {
    var p = new URLSearchParams(window.location.search);
    p.delete('page');
    p.delete('section_id');
    return p;
  };

  Collection.prototype.paramsFromForm = function (form) {
    var p = this.currentParams();
    // Replace every filter parameter with what this form holds; keep sort and anything else as is.
    Array.from(p.keys()).forEach(function (k) { if (k.indexOf('filter.') === 0) p.delete(k); });
    new FormData(form).forEach(function (value, key) {
      if (key === 'sort_by') return;
      if (String(value).trim() === '') return; // empty price boxes are not sent
      p.append(key, value);
    });
    return p;
  };

  Collection.prototype.sortParams = function (value, defaultValue) {
    var p = this.currentParams();
    if (!value || value === defaultValue) p.delete('sort_by');
    else p.set('sort_by', value);
    return p;
  };

  Collection.prototype.urlFor = function (params) {
    var q = params.toString();
    return window.location.pathname + (q ? '?' + q : '');
  };

  Collection.prototype.sectionUrl = function (url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.pathname + u.search;
  };

  /* ---------- Rendering ---------- */
  Collection.prototype.fetchSection = function (url) {
    var self = this;
    if (this.busy) this.busy.abort();
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    this.busy = ctrl;
    return fetch(this.sectionUrl(url), ctrl ? { signal: ctrl.signal } : {})
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        if (self.busy === ctrl) self.busy = null;
        return new DOMParser().parseFromString(html, 'text/html');
      });
  };

  Collection.prototype.render = function (url, push) {
    var self = this;
    var results = this.root.querySelector('[data-ss-sync="results"]');
    if (results) results.setAttribute('aria-busy', 'true');
    return this.fetchSection(url).then(function (doc) {
      var fresh = doc.querySelector('[data-ss-coll]');
      if (!fresh) throw new Error('section not found');
      self.sync(fresh);
      if (push) window.history.pushState({ ssColl: true }, '', url);
      var count = self.root.querySelector('[data-ss-sync="count"]');
      self.announce(count ? count.textContent.trim() : '');
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      window.location.href = url; // fall back to a normal page load
    }).then(function () {
      var r = self.root.querySelector('[data-ss-sync="results"]');
      if (r) r.setAttribute('aria-busy', 'false');
    });
  };

  // Replace every [data-ss-sync] region with the server's version, keeping open dropdowns and focus.
  Collection.prototype.sync = function (fresh) {
    var root = this.root;
    var active = document.activeElement;
    var activeId = active && root.contains(active) ? active.id : '';
    var open = Array.from(root.querySelectorAll('details[data-ss-dd][open]')).map(function (d) { return d.getAttribute('data-ss-dd'); });
    root.querySelectorAll('[data-ss-sync]').forEach(function (el) {
      var key = el.getAttribute('data-ss-sync');
      var src = fresh.querySelector('[data-ss-sync="' + key + '"]');
      if (src) el.innerHTML = src.innerHTML;
    });
    // Sort controls: reflect the active sort.
    var current = new URLSearchParams(window.location.search).get('sort_by');
    var freshSelect = fresh.querySelector('select[data-ss-sort]');
    var value = freshSelect ? freshSelect.value : current;
    root.querySelectorAll('select[data-ss-sort]').forEach(function (s) { if (value) s.value = value; });
    root.querySelectorAll('input[type="radio"][data-ss-sort]').forEach(function (r) { r.checked = r.value === value; });
    open.forEach(function (k) { var d = root.querySelector('details[data-ss-dd="' + k + '"]'); if (d) d.open = true; });
    if (activeId) { var el = document.getElementById(activeId); if (el) el.focus({ preventScroll: true }); }
  };

  Collection.prototype.announce = function (text) {
    var live = this.root.querySelector('[data-ss-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = text; }, 60);
  };

  /* ---------- Load More ---------- */
  Collection.prototype.loadMore = function () {
    var self = this;
    if (this.moreBusy) return;
    var btn = this.root.querySelector('[data-ss-more]');
    var wrap = this.root.querySelector('[data-ss-more-wrap]');
    var grid = this.root.querySelector('[data-ss-grid]');
    if (!btn || !wrap || !grid) return;
    var err = wrap.querySelector('[data-ss-err]');
    this.moreBusy = true;
    btn.setAttribute('aria-disabled', 'true');
    if (err) err.hidden = true;
    var url = btn.getAttribute('href');
    fetch(this.sectionUrl(url))
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('[data-ss-grid] > [data-ss-pid]');
        var firstNew = null;
        items.forEach(function (li) {
          var pid = li.getAttribute('data-ss-pid');
          if (grid.querySelector('[data-ss-pid="' + pid + '"]')) return; // never show a product twice
          var node = document.importNode(li, true);
          grid.appendChild(node);
          if (!firstNew) firstNew = node;
        });
        var total = parseInt(wrap.getAttribute('data-total'), 10) || 0;
        var from = parseInt(wrap.getAttribute('data-from'), 10) || 1;
        var shown = from - 1 + grid.querySelectorAll('[data-ss-pid]').length;
        var label = 'Showing ' + (from > 1 ? from + '–' : '') + shown + ' of ' + total;
        var shownEl = wrap.querySelector('[data-ss-shown]');
        if (shownEl) shownEl.textContent = label;
        var bar = wrap.querySelector('[data-ss-progress]');
        if (bar && total) bar.style.width = Math.min(100, Math.round((shown / total) * 100)) + '%';
        var nextBtn = doc.querySelector('[data-ss-more]');
        if (nextBtn) {
          btn.setAttribute('href', nextBtn.getAttribute('href'));
          btn.removeAttribute('aria-disabled');
        } else {
          btn.remove();
        }
        if (firstNew) {
          var link = firstNew.querySelector('.ss-card__link') || firstNew.querySelector('a');
          if (link) link.focus({ preventScroll: false });
        }
        self.announce(label + ' products');
      })
      .catch(function () {
        btn.removeAttribute('aria-disabled');
        if (err) err.hidden = false;
      })
      .then(function () { self.moreBusy = false; });
  };

  /* ---------- Events ---------- */
  Collection.prototype.bind = function () {
    var self = this;
    var root = this.root;
    var debounce = null;

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-ss-sort]')) {
        var p = self.sortParams(t.value, t.getAttribute('data-default'));
        self.render(self.urlFor(p), true);
        if (t.type === 'radio') self.closeSheet();
        return;
      }
      var form = t.closest('[data-ss-filter-form]');
      if (!form) return;
      if (t.type === 'number') {
        // Price: apply when the shopper leaves the box or presses Enter (see submit), never on every keystroke.
        window.clearTimeout(debounce);
        debounce = window.setTimeout(function () { self.render(self.urlFor(self.paramsFromForm(form)), true); }, 150);
        return;
      }
      self.render(self.urlFor(self.paramsFromForm(form)), true);
    });

    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.matches('[data-ss-filter-form]')) {
        e.preventDefault();
        self.render(self.urlFor(self.paramsFromForm(form)), true);
      } else if (form.matches('[data-ss-sort-form]')) {
        e.preventDefault();
      }
    });

    root.addEventListener('click', function (e) {
      var more = e.target.closest('[data-ss-more]');
      if (more) { e.preventDefault(); self.loadMore(); return; }
      if (e.target.closest('[data-ss-retry]')) { e.preventDefault(); self.loadMore(); return; }
      var link = e.target.closest('a[data-ss-link]');
      if (link) {
        e.preventDefault();
        var u = new URL(link.href, window.location.origin);
        u.searchParams.delete('page');
        self.render(u.pathname + u.search, true);
        return;
      }
      var opener = e.target.closest('[data-ss-open]');
      if (opener) { self.openSheet(opener.getAttribute('data-ss-open'), opener); return; }
      if (e.target.closest('[data-ss-close]')) { self.closeSheet(); return; }
    });

    // Desktop dropdowns: one open at a time; close on outside click and Escape.
    root.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d.matches || !d.matches('details[data-ss-dd]') || !d.open) return;
      root.querySelectorAll('details[data-ss-dd][open]').forEach(function (o) { if (o !== d) o.open = false; });
    }, true);
    document.addEventListener('click', function (e) {
      // Enter in a price box fires a synthetic click on the hidden no-JS Apply button: not an outside click.
      if (e.target.closest && e.target.closest('.ss-coll__nojs')) return;
      root.querySelectorAll('details[data-ss-dd][open]').forEach(function (d) { if (!d.contains(e.target)) d.open = false; });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.openSheetEl) { self.closeSheet(); return; }
      root.querySelectorAll('details[data-ss-dd][open]').forEach(function (d) {
        d.open = false;
        var s = d.querySelector('summary');
        if (s) s.focus();
      });
    });

    window.addEventListener('popstate', function () {
      self.render(window.location.pathname + window.location.search, false);
    });
  };

  /* ---------- Bottom sheets ---------- */
  Collection.prototype.openSheet = function (name, opener) {
    var sheet = this.root.querySelector('[data-ss-sheet="' + name + '"]');
    if (!sheet) return;
    this.closeSheet();
    this.openSheetEl = sheet;
    this.opener = opener || null;
    sheet.hidden = false;
    document.documentElement.classList.add('ss-sheet-open');
    this.prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    var panel = sheet.querySelector('.ss-sheet__panel');
    var first = sheet.querySelector('.ss-sheet__body ' + FOCUSABLE) || panel;
    if (first) first.focus();
    this.trap = function (e) {
      if (e.key !== 'Tab') return;
      var nodes = Array.from(sheet.querySelectorAll(FOCUSABLE)).filter(function (n) { return n.offsetParent !== null; });
      if (!nodes.length) return;
      var a = nodes[0], b = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); b.focus(); }
      else if (!e.shiftKey && document.activeElement === b) { e.preventDefault(); a.focus(); }
    };
    sheet.addEventListener('keydown', this.trap);
  };

  Collection.prototype.closeSheet = function () {
    var sheet = this.openSheetEl;
    if (!sheet) return;
    sheet.hidden = true;
    if (this.trap) sheet.removeEventListener('keydown', this.trap);
    document.documentElement.classList.remove('ss-sheet-open');
    document.documentElement.style.overflow = this.prevOverflow || '';
    this.openSheetEl = null;
    if (this.opener) this.opener.focus();
  };

  /* ---------- Description "Read more" ---------- */
  Collection.prototype.initDescription = function () {
    var box = this.root.querySelector('[data-ss-desc]');
    if (!box) return;
    var text = box.querySelector('[data-ss-desc-text]');
    var btn = box.querySelector('[data-ss-desc-more]');
    box.classList.add('is-clamped');
    if (text.scrollHeight - text.clientHeight > 2) {
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var open = box.classList.toggle('is-clamped') === false;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = open ? 'Read less' : 'Read more';
      });
    } else {
      box.classList.remove('is-clamped');
    }
  };

  /* ---------- Sticky bar position: directly under the visible part of the header ---------- */
  Collection.prototype.initStickyTop = function () {
    var root = this.root;
    var header = document.querySelector('.ss-header-section');
    if (!header) return;
    var ticking = false;
    var update = function () {
      ticking = false;
      var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      root.style.setProperty('--ss-coll-bar-top', bottom + 'px');
    };
    var onScroll = function () { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    header.addEventListener('transitionend', onScroll);
    if ('MutationObserver' in window) new MutationObserver(onScroll).observe(header, { attributes: true, attributeFilter: ['class'] });
    update();
  };

  function init() {
    document.querySelectorAll('[data-ss-coll]').forEach(function (el) {
      if (el.ssCollection) return;
      el.ssCollection = new Collection(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
